import React, { FC, Fragment, useState } from 'react';
import {
  Button,
  createStyles,
  Grid,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { getFullName, IBusiness, ICommission } from '../utils/utils';
import { EditUserCommissionModal } from '../../UserDetails/AdditionalInfos/EditUserCommission/EditUserCommissionModal';
import Tooltip from '@material-ui/core/Tooltip';
import ConfirmDeleteModal from '../../../common/components/ConfirmationModal';
import { Role } from '../../../common/constants';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    left: {
      display: 'flex',
      justifyContent: 'left',
      alignItems: 'center',
    },
    center: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    right: {
      display: 'flex',
      justifyContent: 'right',
      alignItems: 'center',
    },
    label: {
      fontWeight: 'bold',
    },
    icon: {
      height: 15,
      color: '#FF4340',
      cursor: 'pointer',
    },
    input: {
      flex: 1,
    },
    disabled: {
      color: 'black',
    },
    enseigneName: {
      fontWeight: 'bold',
    },
  }),
);

interface Params {
  label: string;
  enseigneName: string | undefined;
  commissions: ICommission[] | null;
  setBusinessState: React.Dispatch<React.SetStateAction<IBusiness>>;
  role: Role[];
  type: 'apaCommissions' | 'mandaCommissions';
  editable: boolean;
  apaId?: number;
}

const BusinessCommissionItem: FC<Params> = (props) => {
  const {
    label,
    enseigneName,
    commissions,
    setBusinessState,
    role,
    type,
    editable,
    apaId,
  } = props;
  const classes = useStyles();
  const [isEditingCommission, setIsEditingCommission] = useState(false);
  const [isRemoveCommission, setIsRemoveCommission] = useState(false);
  const [commissionToEdit, setcommissionToEdit] = useState<
    ICommission | undefined
  >(undefined);

  const handleClose = () => {
    setcommissionToEdit(undefined);
    setIsEditingCommission(false);
  };

  const handleOpen = () => {
    setIsEditingCommission(true);
  };

  const handleEditCommission = (commission: ICommission) => {
    setcommissionToEdit(commission);
    setIsEditingCommission(true);
  };

  const handleRemoveCommission = (commission: ICommission) => {
    setcommissionToEdit(commission);
    setIsRemoveCommission(true);
  };

  const handleConfirm = (commission: ICommission) => {
    setBusinessState((prev) => {
      const newCommissionToAdd: ICommission = {
        id:
          commission?.id === 0
            ? prev[type]
              ? //@ts-ignore
                prev[type]?.length + 1000
              : 1
            : commission?.id,
        amount: commission?.amount,
        beneficiary: {
          id: commission?.beneficiary?.id || 0,
          firstName: commission?.beneficiary?.firstName || '',
          lastName: commission?.beneficiary?.lastName || '',
        },
        isNew: commission?.isNew,
      };

      const commissionToSearch = prev[type]?.find(
        (commission) => commission?.id === newCommissionToAdd?.id,
      );

      // ================== Edit an existing commission ==================

      if (commissionToSearch !== undefined) {
        return {
          ...prev,
          [type]: prev[type]?.map((_commission) => {
            if (_commission?.id === commission?.id)
              return {
                ..._commission,
                beneficiary: newCommissionToAdd?.beneficiary,
                amount: newCommissionToAdd?.amount,
              };
            return _commission;
          }),
        };
      }

      //  ================== Add new commission ==================

      return {
        ...prev,
        // @ts-ignore
        [type]: [...prev[type], ...[newCommissionToAdd]],
      };
    });

    handleClose();
  };

  const onCancelEdit = () => {
    setIsEditingCommission(false);
    setcommissionToEdit(undefined);
  };

  const currentBeneficiaryIds = commissions
    ? commissions
        .map((commission) => commission.beneficiary?.id)
        .filter((id) => !!id)
    : [];

  return (
    <>
      <Grid item xs={12} sm={2} className={classes.left}>
        <Typography>{label} :</Typography>
      </Grid>
      <Grid item xs={12} sm={10} className={classes.left}>
        <Typography className={classes.enseigneName}>{enseigneName}</Typography>
      </Grid>

      {commissions?.map((commission, index) => (
        <Fragment key={commission.id + index}>
          <Grid item xs={12} sm={3} className={classes.left}>
            <Typography className={classes.label}>
              {`Bénéficiaire ${index + 1} :`}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4} className={classes.left}>
            <Typography>
              {getFullName({
                firstName: commission.beneficiary?.firstName || '',
                lastName: commission.beneficiary?.lastName || null,
              })}
            </Typography>
          </Grid>

          <Grid item xs={11} sm={4} className={classes.left}>
            <Typography>{`Commission : ${commission.amount} €`}</Typography>
          </Grid>

          <Grid item xs={1} sm={1} className={classes.right}>
            {editable && (
              <>
                <Tooltip title="Modifier">
                  <EditIcon
                    className={classes.icon}
                    onClick={() => handleEditCommission(commission)}
                  />
                </Tooltip>

                <Tooltip title="Supprimer">
                  <DeleteIcon
                    className={classes.icon}
                    onClick={() => handleRemoveCommission(commission)}
                  />
                </Tooltip>
              </>
            )}
          </Grid>
        </Fragment>
      ))}

      {editable && (
        <Grid item xs={12} className={classes.center}>
          <Button onClick={handleOpen} color="secondary" variant="outlined">
            Ajouter un beneficiaire
          </Button>
        </Grid>
      )}

      <EditUserCommissionModal
        onCancelEdit={onCancelEdit}
        isEditingCommission={isEditingCommission}
        role={role}
        commissionInfo={commissionToEdit}
        onConfirmEditingCommission={handleConfirm}
        currentBeneficiaryIds={currentBeneficiaryIds as number[]}
        userId={apaId}
        onClose={handleClose}
      />

      <ConfirmDeleteModal
        open={isRemoveCommission}
        content={
          <Typography style={{ display: 'flex' }}>
            Supprimer{' '}
            <span style={{ fontWeight: 'bold', marginLeft: 4, marginRight: 4 }}>
              {` ${commissionToEdit?.beneficiary?.firstName.toUpperCase()} ${
                commissionToEdit?.beneficiary?.lastName
              }`}{' '}
            </span>
            <span>?</span>
          </Typography>
        }
        confirmLabel="Oui, supprimer"
        onCancel={() => {
          setIsRemoveCommission(false);
          setcommissionToEdit(undefined);
        }}
        onConfirm={() => {
          setBusinessState((prevState) => {
            if (
              (prevState?.mandaCommissions &&
                prevState?.mandaCommissions?.length > 0) ||
              (prevState?.apaCommissions &&
                prevState?.apaCommissions?.length > 0)
            ) {
              const newApaCommissions =
                prevState?.apaCommissions?.filter(
                  (commission) =>
                    commission?.beneficiary?.id !==
                    commissionToEdit?.beneficiary?.id,
                ) || [];
              const newMandaCommissions =
                prevState?.mandaCommissions?.filter(
                  (commission) =>
                    commission?.beneficiary?.id !==
                    commissionToEdit?.beneficiary?.id,
                ) || [];
              return {
                ...prevState,
                apaCommissions: newApaCommissions,
                mandaCommissions: newMandaCommissions,
              };
            }
            return prevState;
          });
          setIsRemoveCommission(false);
          setcommissionToEdit(undefined);
        }}
      />
    </>
  );
};

export default BusinessCommissionItem;
